import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, Box, Skeleton, Stack, Typography } from '@mui/material'

import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { useGetInternalCategoryListQuery, useSaveInternalCategoryMutation } from '../../api/categories'
import { useGetInternalMarketsQuery } from '../../api/internal-markets'
import { useGetLanguageMapsQuery } from '../../api/language'
import { useGetInternalStoresQuery } from '../../api/stores'
import { DiscardChangesDialog } from '../../components/DiscardChangesDialog/DiscardChangesDialog'
import { InternalListToolbar } from '../../components/InternalListToolbar/InternalListToolbar'
import { CategoryEditDialog } from '../../features/categories/components/CategoryEditDialog/CategoryEditDialog'
import { CategorySection } from '../../features/categories/components/CategorySection/CategorySection'
import {
  CategoryField,
  categoryFieldLabelKeys,
  getLegacyIdZeroCategory,
  getUnstoredCategoryFields,
} from '../../features/categories/components/helpers/categoryHelpers'
import { CategorySaveBody, InternalCategoryRecord } from '../../features/categories/types/Categories'
import { useEditPageLeave } from '../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

type DialogState = { open: boolean; category?: InternalCategoryRecord }

export const CategoriesPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-categories'))
  const { t } = useTranslation()
  const { run, notifyError, notifySuccess } = useMutationFeedback()
  const categoriesQuery = useGetInternalCategoryListQuery()
  const languageMapsQuery = useGetLanguageMapsQuery()
  const storesQuery = useGetInternalStoresQuery()
  const marketsQuery = useGetInternalMarketsQuery()
  const [saveCategory, { isLoading: isSaving }] = useSaveInternalCategoryMutation()

  const [expandedIds, setExpandedIds] = useState<string[]>([])
  const [changedOrderIds, setChangedOrderIds] = useState<string[]>([])
  const [dialogState, setDialogState] = useState<DialogState>({ open: false })

  const categories = useMemo(() => categoriesQuery.data ?? [], [categoriesQuery.data])
  const legacyIdZeroCategory = getLegacyIdZeroCategory(categories)
  const changedOrderNames = categories.filter((category) => changedOrderIds.includes(category.id)).map((category) => category.name)
  const { guard } = useEditPageLeave(changedOrderNames.length > 0)

  const fieldNames = useCallback((fields: CategoryField[]) => fields.map((field) => t(categoryFieldLabelKeys[field])).join(', '), [t])

  const handleExpandedChange = useCallback(
    (categoryId: string, expanded: boolean) =>
      setExpandedIds((ids) => (expanded ? [...ids.filter((id) => id !== categoryId), categoryId] : ids.filter((id) => id !== categoryId))),
    []
  )
  const handleOrderChanged = useCallback(
    (categoryId: string, changed: boolean) =>
      setChangedOrderIds((ids) => {
        if (changed === ids.includes(categoryId)) return ids
        return changed ? [...ids, categoryId] : ids.filter((id) => id !== categoryId)
      }),
    []
  )
  const handleEdit = useCallback((category: InternalCategoryRecord) => setDialogState({ open: true, category }), [])

  const handleSave = async (body: CategorySaveBody) => {
    const isCreate = !body.id
    const saved = await run(saveCategory(body).unwrap(), { error: t('internal-analysts:categories_save_failed') })
    if (!saved.ok) return
    const unstored = getUnstoredCategoryFields(body, saved.result)
    if (unstored.length) {
      notifyError(t('internal-analysts:categories_not_stored', { fields: fieldNames(unstored) }))
      // A retry has to edit the created category rather than create a second one.
      if (isCreate && saved.result.stored) setDialogState({ open: true, category: saved.result.stored })
      return
    }
    notifySuccess(t(isCreate ? 'internal-analysts:categories_added' : 'internal-analysts:categories_saved', { name: body.name }))
    setDialogState({ open: false })
  }

  return (
    <Box>
      <InternalListToolbar
        add={{
          label: t('internal-analysts:categories_add'),
          onClick: () => setDialogState({ open: true }),
          disabled: !categoriesQuery.data || !!legacyIdZeroCategory,
        }}
      />

      {legacyIdZeroCategory && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('internal-analysts:categories_add_blocked', { name: legacyIdZeroCategory.name })}
        </Alert>
      )}
      {changedOrderNames.length > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('internal-analysts:categories_unsaved_orders', { categories: changedOrderNames.join(', ') })}
        </Alert>
      )}

      {categoriesQuery.isLoading ? (
        <Stack spacing={1}>
          {[0, 1, 2, 3].map((index) => (
            <Skeleton key={index} variant="rectangular" height={56} />
          ))}
        </Stack>
      ) : categoriesQuery.isError ? (
        <Typography color="error.main">{t('internal-analysts:categories_load_failed')}</Typography>
      ) : (
        <Box>
          {languageMapsQuery.isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('internal-analysts:categories_language_load_failed')}
            </Alert>
          )}
          {categories.map((category) => (
            <CategorySection
              key={category.id}
              category={category}
              languageMaps={languageMapsQuery.data}
              stores={storesQuery.data}
              markets={marketsQuery.data}
              expanded={expandedIds.includes(category.id)}
              onExpandedChange={handleExpandedChange}
              onEdit={handleEdit}
              onOrderChanged={handleOrderChanged}
            />
          ))}
        </Box>
      )}

      <CategoryEditDialog
        open={dialogState.open && !!languageMapsQuery.data}
        category={dialogState.category}
        languageMaps={languageMapsQuery.data}
        isSaving={isSaving}
        onClose={() => setDialogState({ open: false })}
        onSave={handleSave}
      />
      <DiscardChangesDialog
        guard={guard}
        title={t('internal-analysts:categories_order_discard_title')}
        text={t('internal-analysts:categories_order_discard_confirm', { categories: changedOrderNames.join(', ') })}
        discardText={t('internal-analysts:categories_order_discard')}
      />
    </Box>
  )
}
